import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';
import { useTheme } from '@emotion/react';
import Chart from 'react-apexcharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { useDispatch } from 'store';
import {
  createConfig,
  deleteAllBacktests,
  downloadForeBackTestCSV,
  getBackTest,
  getConfig,
  uploadBackTestsCSV,
} from 'store/slices/evalmetrics';
import PrimaryButton from 'ui-component/button/PrimaryButton';
import AnimateButton from 'ui-component/extended/AnimateButton';
import DropzoneContainer from './DropzoneContainer';

// Constants
const DEFAULT_LIMIT = 1000;
const DEFAULT_SKIP = 0;
const CHART_HEIGHT = 350;

const INITIAL_CONFIG = {
  y_axis: '',
  index1: '',
  index2: '',
  index3: '',
  index1_status: true,
  index2_status: true,
  index3_status: true,
};

const CONFIG_TEXT_FIELDS = [
  { key: 'y_axis', label: 'Y-axis' },
  { key: 'index1', label: 'Index 1' },
  { key: 'index2', label: 'Index 2' },
  { key: 'index3', label: 'Index 3' },
];

const STATUS_TOGGLE_FIELDS = [
  { key: 'index1_status', label: 'Index 1 Status' },
  { key: 'index2_status', label: 'Index 2 Status' },
  { key: 'index3_status', label: 'Index 3 Status' },
];

const parseStatusValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1' || value === 1) return true;
  return false;
};

const getBacktestTime = (row) => {
  const time = Date.parse(row?.date || row?.['Unnamed: 0'] || row?.timestamp);
  return Number.isNaN(time) ? 0 : time;
};

const normalizeListResponse = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];
  return [...list].sort((a, b) => getBacktestTime(a) - getBacktestTime(b));
};

const normalizeConfig = (data = {}) => {
  const normalized = { ...data };

  ['index1_status', 'index2_status', 'index3_status'].forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      normalized[key] = parseStatusValue(data[key]);
    }
  });

  return normalized;
};

const CHART_COLORS = ['#00BAEC', '#77B6EA', '#545454'];

const BACKTEST_SERIES = [
  {
    configKey: 'index1',
    valKeys: ['val_1', 'index1', 'mqe'],
    labelKeys: ['key_1'],
    statusKey: 'index1_status',
    color: CHART_COLORS[0],
    defaultLabel: 'MQE Strategy',
  },
  {
    configKey: 'index2',
    valKeys: ['val_2', 'index2', 'sp500'],
    labelKeys: ['key_2'],
    statusKey: 'index2_status',
    color: CHART_COLORS[1],
    defaultLabel: 'S&P 500',
  },
  {
    configKey: 'index3',
    valKeys: ['val_3', 'index3', 'hari500'],
    labelKeys: ['key_3'],
    statusKey: 'index3_status',
    color: CHART_COLORS[2],
    defaultLabel: 'HFRI 500',
  },
];

const hasResponseField = (list, fields) =>
  fields.some((field) => list.some((item) => item && field in item));

const getBacktestValue = (item, series) => {
  for (const key of series.valKeys) {
    if (item && key in item) return item[key];
  }
  return null;
};

const resolveSeriesLabel = (apiLabel, configLabel, defaultLabel) => {
  if (typeof apiLabel === 'number' && apiLabel === 0) {
    const configText = String(configLabel || '').trim();
    return configText || defaultLabel;
  }

  const apiText = String(apiLabel ?? '').trim();
  if (apiText) return apiText;

  const configText = String(configLabel || '').trim();
  if (configText) return configText;

  return defaultLabel;
};

const getBacktestSeriesLabel = (list, series, config) => {
  for (const labelKey of series.labelKeys) {
    const item = list.find((row) => row && labelKey in row);
    if (item) {
      return resolveSeriesLabel(
        item[labelKey],
        config[series.configKey],
        series.defaultLabel
      );
    }
  }

  return resolveSeriesLabel(
    undefined,
    config[series.configKey],
    series.defaultLabel
  );
};

const getBacktestDateLabel = (row) => {
  const value = row?.date || row?.['Unnamed: 0'];
  const text = String(value ?? '').trim();
  return text || 'N/A';
};

const buildActiveIndexes = (config, backTestsList = []) => {
  const list = normalizeListResponse(backTestsList);

  return BACKTEST_SERIES.filter((series) => {
    if (!hasResponseField(list, series.valKeys)) return false;
    if (config[series.statusKey] === false) return false;
    return true;
  }).map((series) => ({
    key: series.configKey,
    label: getBacktestSeriesLabel(list, series, config),
    color: series.color,
    series,
  }));
};

const ChartSettingsForm = ({ tempConfig, onConfigChange, onStatusToggle }) => (
  <Grid container spacing={2} sx={{ mt: 1 }}>
    {CONFIG_TEXT_FIELDS.map((field) => (
      <Grid item xs={12} key={field.key}>
        <InputLabel>{field.label}</InputLabel>
        <FormControl fullWidth>
          <TextField
            variant="outlined"
            placeholder={`Enter ${field.label.toLowerCase()}`}
            name={field.key}
            value={tempConfig[field.key] || ''}
            onChange={onConfigChange}
          />
        </FormControl>
      </Grid>
    ))}
    {STATUS_TOGGLE_FIELDS.map((field) => (
      <Grid item xs={12} key={field.key}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <InputLabel sx={{ mb: 0 }}>{field.label}</InputLabel>
          <Switch
            checked={!!tempConfig[field.key]}
            onChange={() => onStatusToggle(field.key)}
            color="primary"
          />
        </Box>
      </Grid>
    ))}
  </Grid>
);

const DIALOG_TYPES = {
  NONE: 'NONE',
  UPLOAD: 'UPLOAD',
  SETTINGS: 'SETTINGS',
  DELETE_ALL: 'DELETE_ALL',
};

// Dialog state reducer
const dialogReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return { type: action.dialogType };
    case 'CLOSE_DIALOG':
      return { type: DIALOG_TYPES.NONE };
    default:
      return state;
  }
};

// Utility function for CSV download
const downloadCSVFile = (csvData, filename) => {
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Chart configuration generator
const generateChartOptions = (dates, xAxisLabel, activeIndexes = []) => ({
  chart: {
    type: 'area',
    height: CHART_HEIGHT,
    zoom: { enabled: true },
    toolbar: {
      show: false,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
      },
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2,
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.1,
      stops: [0, 90, 100],
    },
  },
  markers: {
    size: 4,
    colors: ['#fff'],
    strokeColors: activeIndexes.map((index) => index.color),
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  grid: {
    row: {
      colors: ['#f3f3f3', 'transparent'],
      opacity: 0.5,
    },
    borderColor: '#e0e0e0',
  },
  xaxis: {
    type: 'category',
    categories: dates,
    title: {
      text: 'Date',
      style: {
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    labels: {
      rotate: -45,
      rotateAlways: false,
      hideOverlappingLabels: true,
      style: {
        fontSize: '12px',
      },
    },
  },
  yaxis: {
    title: {
      text: xAxisLabel || 'Value',
      style: {
        fontSize: '14px',
        fontWeight: 600,
      },
    },
    labels: {
      formatter: (value) => (value ? value.toFixed(2) : '0.00'),
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    x: {
      formatter: (value) => value || 'N/A',
    },
    y: {
      formatter: (val) => (val ? `${val.toFixed(2)}%` : 'N/A'),
    },
  },
  legend: {
    show: activeIndexes.length > 0,
    position: 'top',
    horizontalAlign: 'right',
    fontSize: '14px',
    markers: {
      width: 12,
      height: 12,
      radius: 12,
    },
  },
  colors: activeIndexes.map((index) => index.color),
});

const BackTests = ({ selectedSubCategory }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { categoryId, subCategoryId } = useParams();

  // Computed values
  const assetId = useMemo(
    () => subCategoryId || categoryId,
    [subCategoryId, categoryId]
  );
  const scope = useMemo(
    () => (subCategoryId ? 'asset' : 'portfolio'),
    [subCategoryId]
  );
  const assetType = useMemo(
    () => (subCategoryId ? 'assets' : 'portfolios'),
    [subCategoryId]
  );

  // Query key
  const backtestsQueryKey = useMemo(() => ['backtests', assetId], [assetId]);

  // State
  const [dialogState, dialogDispatch] = useReducer(dialogReducer, {
    type: DIALOG_TYPES.NONE,
  });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState({
    ...INITIAL_CONFIG,
    scope,
    assetId,
  });
  const [tempConfig, setTempConfig] = useState({
    ...INITIAL_CONFIG,
    scope,
    assetId,
  });

  // Fetch backtest data using React Query
  // Enabled by default (will be controlled by React Query cache)
  const { data: backTestsList = [], isLoading } = useQuery({
    queryKey: backtestsQueryKey,
    queryFn: async () => {
      const payload = {
        assetId,
        limit: DEFAULT_LIMIT,
        skip: DEFAULT_SKIP,
        type: subCategoryId ? 'assets' : 'portfolio',
      };

      const response = await dispatch(getBackTest(payload));
      return normalizeListResponse(response?.data);
    },
    enabled: !!assetId, // Only fetch when assetId is available
    refetchOnWindowFocus: false,
    onError: () => {
      toast.error('Failed to fetch backtest data');
    },
  });

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      const response = await dispatch(getConfig({ scope, assetId }));

      if (response?.status === 200 && response?.data) {
        setConfig((prev) => ({
          ...prev,
          ...normalizeConfig(response.data),
          scope,
          assetId,
        }));
      }
    } catch (error) {
      // Silent fail - use default config
    }
  }, [scope, assetId, dispatch]);

  // Initial config fetch
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const activeIndexes = useMemo(
    () => buildActiveIndexes(config, backTestsList),
    [config, backTestsList]
  );

  const chartDates = useMemo(
    () => backTestsList.map(getBacktestDateLabel),
    [backTestsList]
  );

  // Chart options and series
  const chartOptions = useMemo(
    () => generateChartOptions(chartDates, config.y_axis, activeIndexes),
    [chartDates, config.y_axis, activeIndexes]
  );

  const chartSeries = useMemo(
    () =>
      activeIndexes.map((index) => ({
        name: index.label,
        data: backTestsList.map((item) => getBacktestValue(item, index.series)),
      })),
    [activeIndexes, backTestsList]
  );

  // Dialog handlers
  const handleCloseDialog = useCallback(() => {
    dialogDispatch({ type: 'CLOSE_DIALOG' });
    setFile(null);
    setTempConfig(config);
  }, [config]);

  const handleOpenUploadDialog = useCallback(() => {
    dialogDispatch({ type: 'OPEN_DIALOG', dialogType: DIALOG_TYPES.UPLOAD });
  }, []);

  const handleOpenSettingsDialog = useCallback(() => {
    setTempConfig(config);
    dialogDispatch({ type: 'OPEN_DIALOG', dialogType: DIALOG_TYPES.SETTINGS });
  }, [config]);

  const handleOpenDeleteAllDialog = useCallback(() => {
    dialogDispatch({
      type: 'OPEN_DIALOG',
      dialogType: DIALOG_TYPES.DELETE_ALL,
    });
  }, []);

  // File handlers
  const handleUploadCSV = useCallback(async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await dispatch(uploadBackTestsCSV(assetId, formData));

      if (response?.data?.message === 'Invalid CSV Format') {
        toast.error('Invalid CSV Format');
      } else {
        toast.success('CSV uploaded successfully');
        // Invalidate backtests query to trigger auto-refresh in both chart and table
        queryClient.invalidateQueries(backtestsQueryKey);
        handleCloseDialog();
      }
    } catch (error) {
      toast.error('Error uploading file');
    } finally {
      setIsSaving(false);
    }
  }, [
    file,
    assetId,
    dispatch,
    queryClient,
    backtestsQueryKey,
    handleCloseDialog,
  ]);

  const handleDownloadCSV = useCallback(async () => {
    try {
      const response = await dispatch(
        downloadForeBackTestCSV(assetId, assetType)
      );

      downloadCSVFile(response.data, `backtest_${assetId}.csv`);
      toast.success('Backtest downloaded successfully');
    } catch (error) {
      toast.error('Error downloading file');
    }
  }, [assetId, assetType, dispatch]);

  // Config handlers
  const handleConfigChange = useCallback((e) => {
    const { name, value } = e.target;
    setTempConfig((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleStatusToggle = useCallback((key) => {
    setTempConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSaveConfig = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...tempConfig,
        ref_id: tempConfig.ref_id || assetId,
        scope,
        assetId,
      };

      const response = await dispatch(createConfig(payload));

      if (response?.status === 200) {
        toast.success('Chart settings saved successfully');
        setConfig(tempConfig);
        handleCloseDialog();
        await queryClient.refetchQueries({ queryKey: backtestsQueryKey });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save chart settings');
    } finally {
      setIsSaving(false);
    }
  }, [
    tempConfig,
    assetId,
    scope,
    dispatch,
    handleCloseDialog,
    queryClient,
    backtestsQueryKey,
  ]);

  // Delete all handler
  const handleDeleteAllBacktests = useCallback(async () => {
    setIsSaving(true);
    try {
      await dispatch(deleteAllBacktests({ assetId }));

      toast.success('All backtests deleted successfully');
      // Invalidate backtests query to trigger auto-refresh in both chart and table
      queryClient.invalidateQueries(backtestsQueryKey);
      handleCloseDialog();
    } catch (error) {
      toast.error('Failed to delete all backtests');
    } finally {
      setIsSaving(false);
    }
  }, [assetId, dispatch, queryClient, backtestsQueryKey, handleCloseDialog]);

  // Dialog states
  const isUploadDialogOpen = dialogState.type === DIALOG_TYPES.UPLOAD;
  const isSettingsDialogOpen = dialogState.type === DIALOG_TYPES.SETTINGS;
  const isDeleteAllDialogOpen = dialogState.type === DIALOG_TYPES.DELETE_ALL;

  // Render loading state
  if (isLoading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Skeleton variant="text" width={300} height={40} />
            <Stack direction="row" gap={2}>
              <Skeleton variant="rectangular" width={100} height={36} />
              <Skeleton variant="rectangular" width={100} height={36} />
              <Skeleton variant="rectangular" width={100} height={36} />
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" width="100%" height={CHART_HEIGHT} />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {/* Header */}
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h6" fontWeight={500}>
            Backtest of MQE Algorithm
            {selectedSubCategory?.name && ` on ${selectedSubCategory.name}`}
          </Typography>
          <Stack direction="row" gap={2}>
            <AnimateButton>
              <PrimaryButton
                title="Import Backtest"
                onClick={handleOpenUploadDialog}
              />
            </AnimateButton>
            <AnimateButton>
              <PrimaryButton
                title="Export Backtest"
                onClick={handleDownloadCSV}
              />
            </AnimateButton>
            <AnimateButton>
              <PrimaryButton
                title="Settings"
                onClick={handleOpenSettingsDialog}
              />
            </AnimateButton>
            <Button
              variant="outlined"
              onClick={handleOpenDeleteAllDialog}
              startIcon={<DeleteIcon />}
              disabled={backTestsList.length === 0}
              sx={{
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.light',
                },
                '&.Mui-disabled': {
                  borderColor: 'action.disabled',
                  color: 'action.disabled',
                },
              }}
            >
              Delete All
            </Button>
          </Stack>
        </Stack>
      </Grid>

      {/* Chart */}
      <Grid item xs={12} mt={4}>
        {backTestsList.length === 0 ? (
          <Box
            sx={{
              height: CHART_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #ccc',
              borderRadius: 1,
              backgroundColor: '#f9f9f9',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No backtest data available
            </Typography>
          </Box>
        ) : (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={CHART_HEIGHT}
          />
        )}
      </Grid>

      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="upload-dialog-title"
      >
        <DialogTitle id="upload-dialog-title">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Upload Backtest CSV</Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ color: theme.palette.grey[500] }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <DropzoneContainer
              file={file}
              setFile={setFile}
              uploadProgress={uploadProgress}
              setUploadProgress={setUploadProgress}
              title="Drag and drop a CSV file here, or click the button below to select one"
              buttonName="Upload CSV"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <AnimateButton>
            <PrimaryButton
              title="Import"
              onClick={handleUploadCSV}
              disabled={!file || isSaving}
              style={{ padding: '8px 20px' }}
            />
          </AnimateButton>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={isSettingsDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="settings-dialog-title"
      >
        <DialogTitle id="settings-dialog-title">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Chart Settings</Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ color: theme.palette.grey[500] }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ChartSettingsForm
            tempConfig={tempConfig}
            onConfigChange={handleConfigChange}
            onStatusToggle={handleStatusToggle}
          />
        </DialogContent>
        <DialogActions>
          <Box display="flex" gap={2} px={2}>
            <AnimateButton>
              <PrimaryButton
                title="Save"
                onClick={handleSaveConfig}
                disabled={isSaving}
              />
            </AnimateButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete All Dialog */}
      <Dialog
        open={isDeleteAllDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="delete-all-dialog-title"
      >
        <DialogTitle id="delete-all-dialog-title">
          Delete All Backtests
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all backtests (
            {backTestsList.length} items)? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            onClick={handleCloseDialog}
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllBacktests}
            disabled={isSaving}
            sx={{ color: 'white' }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BackTests;
